// ─────────────────────────────────────────────
//  AuthContext.js
//  Global authentication state provider.
//  Wrap <App /> with <AuthProvider> in index.js.
// ─────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../config/firebaseconfig";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true); // true until Firebase resolves

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe; // cleanup on unmount
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const value = { user, loading, login, signup, logout, resetPassword };

  return (
    <AuthContext.Provider value={value}>
      {/* Don't render children until Firebase resolves auth state */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Custom hook — use anywhere: const { user, login, logout } = useAuth();
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
